// The owner's meetings API (`apps/server/src/http/meetings.ts`,
// `apps/server/src/http/google-auth.ts`). Every answer is parsed with the
// shared DTO (`@loom/shared/meetings`), so a field the server adds without
// declaring it there never reaches the page.
//
// A failed write answers a closed code (`{error: "<code>"}`), sometimes with
// the issue as the forge has it now (`{error, issue}` on a 409: the issue
// changed under the click). `failureOf` reads both; the page shows catalog
// copy for the code, never the body.

import { z } from "zod";

import {
  MeetingDetailDto,
  MeetingDoubtAnswerDto,
  MeetingFileAllDto,
  MeetingImportableDto,
  MeetingImportDto,
  MeetingIssueRowDto,
  MeetingLaunchDto,
  MeetingLinkCreatedDto,
  MeetingLinkStateDto,
  MeetingListDto,
  MeetingListItemDto,
  MeetingReprocessDto,
  MeetingRetryDto,
  MeetingTicketUpdatePreviewDto,
  MeetingTranscriptDto,
  MeetingsStatusDto,
  type MeetingTicketUpdateRequest,
  type MeetingAnswerRequest,
  type MeetingDoubtAnswer,
  type MeetingDoubtAnswerRequest,
  type MeetingImportRequest,
  type MeetingIssuePatchRequest,
  type MeetingLaunchRequest,
  type RecordingStart,
  type TicketClass,
} from "@loom/shared/meetings";

import { HttpError, httpDelete, httpGet, httpPatch, httpPost, httpPostNoContent, httpPut } from "../../lib/http";

/**
 * Where "Google verbinden" goes (the account page only): a full-page
 * navigation (OAuth), never fetch. `returnTo` is the account page's own
 * path and query; the server keeps it only when it is `/account`.
 */
export const googleConnectHref = (returnTo: string): string =>
  `/auth/google/start?returnTo=${encodeURIComponent(returnTo)}`;

/** The query key `/auth/google/callback` lands on `/account` with. */
export const GOOGLE_RESULT_PARAM = "google";

const base = (meetingId: string): string => `/api/meetings/${encodeURIComponent(meetingId)}`;
const issuePath = (meetingId: string, iid: number): string => `${base(meetingId)}/issues/${iid}`;

/** `withEmail`: the account page's row also wants the Google account's e-mail. */
export const readMeetingsStatus = (
  opts: { readonly withEmail?: boolean } = {},
): Promise<MeetingsStatusDto> =>
  httpGet(
    opts.withEmail === true ? "/api/meetings/status?email=1" : "/api/meetings/status",
    MeetingsStatusDto,
  );

/**
 * One project's meetings, newest first; `before` pages back. `none`: the
 * meetings with no project yet (from before every launch named one).
 */
export const listMeetings = (
  projectId: number | "none",
  before?: string,
): Promise<MeetingListDto> => {
  const params = new URLSearchParams({ projectId: String(projectId) });
  if (before !== undefined) params.set("before", before);
  return httpGet(`/api/meetings?${params.toString()}`, MeetingListDto);
};

export const readMeeting = (meetingId: string): Promise<MeetingDetailDto> =>
  httpGet(base(meetingId), MeetingDetailDto);

export const readTranscript = (meetingId: string): Promise<MeetingTranscriptDto> =>
  httpGet(`${base(meetingId)}/transcript`, MeetingTranscriptDto);

/**
 * "Meeting starten" for the page's project (always one: the server answers
 * 400 without). `recordingStart` `manual` makes the space with auto-recording
 * off, so the host starts it in Meet; `auto` lets Meet start it on its own.
 */
export const launchMeeting = (
  projectId: number,
  recordingStart: RecordingStart,
): Promise<MeetingLaunchDto> => {
  const body: MeetingLaunchRequest = { projectId, recordingStart };
  return httpPost("/api/meetings", body, MeetingLaunchDto);
};

/**
 * Meetings recorded without Loom that the owner may process (the import
 * page); `code`: only the meetings with this Meet link or code (the server
 * parses it again and refuses anything that is not one).
 */
export const listImportable = (code?: string): Promise<MeetingImportableDto> =>
  httpGet(
    code === undefined
      ? "/api/meetings/importable"
      : `/api/meetings/importable?${new URLSearchParams({ code }).toString()}`,
    MeetingImportableDto,
  );

/**
 * Process one recorded meeting for the page's project (always one: the
 * server answers 400 without). The server builds the meeting around the
 * conference from Google's own records.
 */
export const importMeeting = (
  conferenceName: string,
  projectId: number,
): Promise<MeetingImportDto> => {
  const body: MeetingImportRequest = { conferenceName, projectId };
  return httpPost("/api/meetings/import", body, MeetingImportDto);
};

export type IssueAction = "accept" | "decline" | "restore";

export const issueAction = (
  meetingId: string,
  iid: number,
  action: IssueAction,
): Promise<MeetingIssueRowDto> =>
  httpPost(`${issuePath(meetingId, iid)}/${action}`, {}, MeetingIssueRowDto);

export const editMeetingIssue = (
  meetingId: string,
  iid: number,
  patch: MeetingIssuePatchRequest,
): Promise<MeetingIssueRowDto> => httpPatch(issuePath(meetingId, iid), patch, MeetingIssueRowDto);

/**
 * An open question answered, or "Weiss ich nicht" (`{skip: true}`), refine
 * light. Loom keeps the answer for refine and writes it into the issue's
 * meeting block; the row's `bodySync` says whether the block took it (the
 * same call again writes it).
 */
export const answerMeetingQuestion = (
  meetingId: string,
  iid: number,
  field: string,
  body: MeetingAnswerRequest,
): Promise<MeetingIssueRowDto> =>
  httpPut(`${issuePath(meetingId, iid)}/questions/${encodeURIComponent(field)}`, body, MeetingIssueRowDto);

export const promoteTopic = (
  meetingId: string,
  topicId: string,
  as: TicketClass,
): Promise<MeetingIssueRowDto> =>
  httpPost(
    `${base(meetingId)}/topics/${encodeURIComponent(topicId)}/promote`,
    { as },
    MeetingIssueRowDto,
  );

/**
 * The doubt card: the owner's answer to one question (`doubtId`, its first
 * item's topic). An answer that files drafts and files on the server; on an
 * item already filed it changes that issue (`applied`, `waiting`,
 * `effectFailed`). `leaveOut`: items of a question about several the answer
 * does not file (or closes, when filed).
 */
export const answerMeetingDoubt = (
  meetingId: string,
  doubtId: string,
  answer: MeetingDoubtAnswer,
  leaveOut: readonly string[] = [],
): Promise<MeetingDoubtAnswerDto> => {
  const body: MeetingDoubtAnswerRequest = leaveOut.length > 0 ? { answer, leaveOut: [...leaveOut] } : { answer };
  return httpPost(`${base(meetingId)}/doubts/${encodeURIComponent(doubtId)}`, body, MeetingDoubtAnswerDto);
};

export const setMeetingProject = (
  meetingId: string,
  projectId: number,
): Promise<MeetingListItemDto> =>
  httpPut(`${base(meetingId)}/project`, { projectId }, MeetingListItemDto);

export const retryMeeting = (meetingId: string): Promise<MeetingRetryDto> =>
  httpPost(`${base(meetingId)}/retry`, {}, MeetingRetryDto);

/**
 * "Neu verarbeiten": the meeting runs the current triage again. Nothing is
 * filed on its own; a second call while it runs answers the running one.
 */
export const reprocessMeeting = (meetingId: string): Promise<MeetingReprocessDto> =>
  httpPost(`${base(meetingId)}/reprocess`, {}, MeetingReprocessDto);

/** "Anlegen": file one new row of a reprocess run; answers the row as it is now. */
export const fileMeetingTicket = (meetingId: string, ticketId: string): Promise<MeetingIssueRowDto> =>
  httpPost(`${base(meetingId)}/tickets/${encodeURIComponent(ticketId)}/file`, {}, MeetingIssueRowDto);

/**
 * "Ticket aktualisieren", first step: the issue's body now next to the whole
 * body the update would write, and whether the ticket was edited since.
 */
export const previewTicketUpdate = (
  meetingId: string,
  ticketId: string,
): Promise<MeetingTicketUpdatePreviewDto> =>
  httpGet(
    `${base(meetingId)}/tickets/${encodeURIComponent(ticketId)}/update-preview`,
    MeetingTicketUpdatePreviewDto,
  );

/**
 * "Ticket aktualisieren", confirmed: sends the hash of the body the preview
 * showed (a body changed since is refused); answers the row as it is now.
 */
export const updateTicket = (
  meetingId: string,
  ticketId: string,
  currentBodySha256: string,
): Promise<MeetingIssueRowDto> => {
  const body: MeetingTicketUpdateRequest = { currentBodySha256 };
  return httpPost(`${base(meetingId)}/tickets/${encodeURIComponent(ticketId)}/update`, body, MeetingIssueRowDto);
};

/** "Alle neuen anlegen": every new row that is no issue of the meeting yet. */
export const fileAllMeetingTickets = (meetingId: string): Promise<MeetingFileAllDto> =>
  httpPost(`${base(meetingId)}/tickets/file-all`, {}, MeetingFileAllDto);

export const deleteMeeting = (meetingId: string): Promise<void> => httpDelete(base(meetingId));

/** "Link teilen": a new magic link (the old one stops); the token is in this answer only. */
export const createMeetingLink = (meetingId: string): Promise<MeetingLinkCreatedDto> =>
  httpPost(`${base(meetingId)}/link`, {}, MeetingLinkCreatedDto, { cache: "no-store" });

/** "Verlängern": the same link, valid for the full span from now. */
export const renewMeetingLink = (meetingId: string): Promise<MeetingLinkStateDto> =>
  httpPost(`${base(meetingId)}/link/renew`, {}, MeetingLinkStateDto);

/** "Widerrufen" (204, no body). */
export const revokeMeetingLink = (meetingId: string): Promise<void> =>
  httpPostNoContent(`${base(meetingId)}/link/revoke`, {});

const GoogleDisconnectDto = z.object({
  ok: z.boolean(),
  revokedAtGoogle: z.boolean().optional(),
});

export const disconnectGoogle = (): Promise<z.infer<typeof GoogleDisconnectDto>> =>
  httpPost("/auth/google/disconnect", {}, GoogleDisconnectDto);

const FailureBody = z.object({
  error: z.string(),
  issue: z.unknown().optional(),
});

/** What a failed call said: its HTTP status, closed code and, on a refused
 *  issue action, the issue as it stands now. */
export interface MeetingFailure {
  readonly status: number | null;
  readonly code: string | null;
  readonly issue: MeetingIssueRowDto | null;
}

export const failureOf = (err: unknown): MeetingFailure => {
  if (!(err instanceof HttpError)) return { status: null, code: null, issue: null };
  let parsed: z.infer<typeof FailureBody> | null = null;
  try {
    const result = FailureBody.safeParse(JSON.parse(err.body ?? ""));
    parsed = result.success ? result.data : null;
  } catch {
    parsed = null;
  }
  const issue =
    parsed?.issue === undefined ? null : MeetingIssueRowDto.safeParse(parsed.issue);
  return {
    status: err.status,
    code: parsed?.error ?? null,
    issue: issue !== null && issue.success ? issue.data : null,
  };
};
